// Shared money helpers — a single source of truth for agora rounding and the
// VAT multiplier. The same two formulas were previously re-derived inline in
// creditMatch.js, creditKind.js, CreditsPage.jsx and LinkOpenCreditsModal.jsx;
// keeping them here prevents the kind of net/gross drift that caused the
// original credit-VAT mismatch. Behaviour matches the inline versions replaced.

// Agora rounding, decimal-safe (Codex review, PR #273, 15/10/2026).
// `Math.round(n * 100) / 100` rounds the BINARY approximation — 1.005 is stored
// as 1.00499999…, so it came out 1.00 while Intl showed 1.01. The ×100 is
// cleaned to 15 significant digits first (100.49999999999999 → 100.5), then
// rounded half away from zero: 1.005 → 1.01, 10.075 → 10.08, -1.005 → -1.01.
// Must stay identical to server/lib/roundMoney.js — line, VAT and total
// formulas are computed on both sides (lineCalc ↔ estimatePrice,
// roundDocTotal ↔ computeDocTotal). Junk and blanks read as 0.
export const round2 = (n) => {
  const v = parseFloat(n);
  if (!Number.isFinite(v)) return 0;
  const cents = Number((Math.abs(v) * 100).toPrecision(15));
  return (Math.sign(v) * Math.round(cents)) / 100 || 0;
};

// VAT multiplier from a rate in percent (e.g. 18 → 1.18). Falls back to 18 when
// the rate is missing or blank, matching the app-wide default.
export const vatMultiplier = (rate) => 1 + (parseFloat(rate) || 18) / 100;

// Whole shekels, the way the payment screens (/suppliers, /suppliers/paid,
// /credits) show money (owner, 14/10/2026): up to 49 agorot rounds down, 50 and
// above rounds up — ₪232.80 is paid as 233, ₪451.40 as 451. Symmetric for
// negatives (-232.50 → -233), never "-0". The 1e-6 absorbs float noise from a
// SUM over REAL columns (232.49999999999997 is ₪232.50 → 233) without rounding
// to agorot first, which would double-round a 3-decimal credit total
// (10.495 → 10.50 → 11 instead of 10). Display/payment only — stored totals
// keep their agorot (roundDocTotal).
export const roundWhole = (n) => {
  const v = parseFloat(n) || 0;
  return (Math.sign(v) * Math.round(Math.abs(v) + 1e-6)) || 0;
};
